import React from 'react';
import type { RequestOptions } from 'searchkit';

import { SearchkitWrapper } from '@uncinc/react-searchkit';

import { APP_OPTIONS, SEARCHKIT_HOST_URL } from '../../../config/constants';
import type { MetaParagraphsContentTypes } from '../../../types/uncinc_profile/paragraphs';
import { facetAttributes, resultAttributes, searchAttributes } from './config';

// Subcomponents.
import EmptyState from './EmptyState';
import Facets from './Facets';
import Inner from './Inner';

interface Subcomponents {
  EmptyState: typeof EmptyState;
  Facets: typeof Facets;
  Inner: typeof Inner;
}

export interface MetaSearchProps {
  requestOptions: RequestOptions;
  contentType: MetaParagraphsContentTypes;
  sortingList?: number[];
  hitsPerPage: number;
  withSearchbar?: boolean;
  withFacets?: boolean;
  withPagination?: boolean;
  children?: React.ReactNode;
}

const MetaSearch: React.FC<MetaSearchProps> & Subcomponents = ({
  requestOptions,
  sortingList = undefined,
  hitsPerPage,
  withSearchbar = false,
  withFacets = false,
  withPagination = false,
  children,
}) => (
  <SearchkitWrapper
    connection={{
      host: SEARCHKIT_HOST_URL,
    }}
    indexName={APP_OPTIONS?.parameters.searchkitIndexAllContent}
    routing={false}
    hitsPerPage={hitsPerPage}
    requestOptions={requestOptions}
    searchSettings={{
      search_attributes: searchAttributes,
      result_attributes: resultAttributes,
      facet_attributes: facetAttributes,
      sorting: {
        default: [
          // Articles and vacancies.
          {
            field: 'publish_date',
            order: 'desc',
          },
          // Projects.
          {
            field: 'project_duration_start_date',
            order: 'desc',
          },
          // Other nodes.
          {
            field: 'created',
            order: 'desc',
          },
          // Profiles.
          {
            field: 'head_of_department',
            order: 'desc',
          },
          {
            field: 'last_name',
            order: 'asc',
          },
          // Taxonomy terms.
          {
            field: 'taxonomy_weight',
            order: 'asc',
          },
        ],
      },
    }}
  >
    {children || (
      <Inner
        sortingList={sortingList}
        withSearchbar={withSearchbar}
        withFacets={withFacets}
        withPagination={withPagination}
      />
    )}
  </SearchkitWrapper>
);

MetaSearch.EmptyState = EmptyState;
MetaSearch.Facets = Facets;
MetaSearch.Inner = Inner;

export default MetaSearch;
